import {useEffect, useMemo, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {Gift, Plus, ShieldCheck, WalletCards} from "lucide-react";
import {AdminTable} from "../../shared/components/AdminTable";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";
import {PageToolbar, ToolbarButton, ToolbarInput} from "../../shared/components/PageToolbar";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {walletLabel, walletListValue, walletTotal} from "../../shared/wallets";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {asList, formatMoney, formatNumber, getCount} from "../../shared/utils/format";
import {apiGet} from "../../shared/api/client";
import {loadUsers} from "./api";
import {mapUserApiError} from "./errors";
import {UserCreateDialog} from "./UserCreateDialog";
import {useUserCountryScope} from "./useUserCountryScope";
import {useTranslation, i18n} from "../../shared/i18n";


const balanceValue = (user, key) => Number(user?.balance?.[key] || 0);

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

// Бэкенд отдаёт next/previous ссылками (DRF): номер страницы достаём из ссылки,
// а в состоянии по-прежнему живёт числовой page — как в OrganizationsPage.
const pageFromLink = (link) => {
    if (!link) return null;
    try {
        const parsed = new URL(link, "https://users.invalid");
        return Number(parsed.searchParams.get("page") || 1);
    } catch (_error) {
        return null;
    }
};

export const UsersWalletsPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const queryClient = useQueryClient();
    const {countryScope, countryId} = useUserCountryScope(capabilities);
    const [draftSearch, setDraftSearch] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [createOpen, setCreateOpen] = useState(false);
    const [createdMessage, setCreatedMessage] = useState("");

    useEffect(() => {
        setPage(1);
        setCreatedMessage("");
    }, [countryScope]);

    const usersQuery = useQuery({
        queryKey: ["admin-users", countryScope, page, search, pageSize],
        queryFn: () => loadUsers({countryId: countryScope, page, search, pageSize}),
        enabled: countryScope !== null,
    });

    const users = asList(usersQuery.data);
    const walletTotals = useMemo(
        () => ({
            main: walletTotal(usersQuery.data, "main") ?? users.reduce((sum, user) => sum + balanceValue(user, "balance_sum"), 0),
            plus: walletTotal(usersQuery.data, "plus") ?? users.reduce((sum, user) => sum + balanceValue(user, "add_sum"), 0),
            extra: walletTotal(usersQuery.data, "extra") ?? users.reduce((sum, user) => sum + balanceValue(user, "dobavka_sum"), 0),
            frozen: walletTotal(usersQuery.data, "frozen") ?? users.reduce((sum, user) => sum + Number(user.ref_sum || 0), 0),
        }),
        [users, usersQuery.data],
    );

    // POST /api/v2/admin/users принимает country_id выбранного скоупа. Без
    // конкретной страны (скоуп «Все страны») бэкенд сажает юзера в город актора.
    const canCreate = capabilities.hasAction("user.manage");
    const countriesQuery = useQuery({
        queryKey: ["admin-countries", capabilities.user?.id],
        queryFn: () => apiGet("/api/v2/admin/properties/countries/list"),
        enabled: Boolean(capabilities.user?.id) && canCreate,
        retry: 1,
    });
    const createCountryName = useMemo(() => {
        if (countryId) {
            const match = (countriesQuery.data || []).find((country) => String(country?.id) === String(countryId));
            if (match?.name) return match.name;
            if (String(capabilities.country?.id) === String(countryId)) {
                return capabilities.country?.name;
            }
            return "";
        }
        return capabilities.country?.name;
    }, [capabilities.country?.id, capabilities.country?.name, countriesQuery.data, countryId]);

    const applySearch = () => {
        setPage(1);
        setSearch(draftSearch.trim());
    };

    const clearSearch = () => {
        setDraftSearch("");
        setSearch("");
        setPage(1);
    };

    const changePageSize = (nextSize) => {
        setPage(1);
        setPageSize(nextSize);
    };

    const columns = [
        {
            key: "user",
            label: t("sections.users.p122"),
            render: (user) => (
                <div>
                    <Link to={`/users/${user.id}`}>{user.name || i18n.t("sections.users.p123")}</Link>
                    <div className="admin-muted">ID {user.id}</div>
                </div>
            ),
        },
        {key: "phone_number", label: t("sections.users.p10"), render: (user) => user.phone_number || "—"},
        {
            key: "main",
            label: walletLabel("main"),
            render: (user) => <span className="admin-strong">{formatMoney(walletListValue(user, "main"))}</span>,
        },
        {key: "plus", label: walletLabel("plus"), render: (user) => formatMoney(walletListValue(user, "plus"))},
        {key: "extra", label: walletLabel("extra"), render: (user) => formatMoney(walletListValue(user, "extra"))},
        {key: "frozen", label: walletLabel("frozen"), render: (user) => formatMoney(walletListValue(user, "frozen"))},
        {
            key: "status",
            label: t("sections.users.p124"),
            render: (user) => {
                const active = ["Активен", "Активный", true].includes(user.user_status);
                return <StatusBadge tone={active ? "success" : "danger"}>{user.user_status || (active ? t("sections.users.p126") : t("sections.users.p127"))}</StatusBadge>;
            },
        },
        {
            key: "actions",
            label: t("sections.users.p128"),
            render: (user) => <Link to={`/users/${user.id}`}>{i18n.t("sections.users.p129")}</Link>,
        },
    ];

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{t("sections.users.p130")}</h1>
                    <p className="admin-page-copy">{t("sections.users.p131")}</p>
                </div>
                {canCreate ? (
                    <button type="button" className="admin-button admin-button-primary" onClick={() => setCreateOpen(true)}>
                        <Plus aria-hidden="true" size={16} /> {t("sections.users.p4")}
                    </button>
                ) : null}
            </header>

            {createdMessage ? <div className="admin-card" role="status">{createdMessage}</div> : null}

            <section className="admin-grid cols-4">
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.users.p132")}</p>
                    <p className="admin-card-value">{formatNumber(getCount(usersQuery.data))}</p>
                    <p className="admin-card-note"><ShieldCheck size={13} /> {t("sections.users.p133")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.users.p134")}</p>
                    <p className="admin-card-value" style={{color: "var(--admin-money)"}}>{formatMoney(walletTotals.main)}</p>
                    <p className="admin-card-note">{t("sections.users.p135")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.users.p136")}</p>
                    <p className="admin-card-value">{formatMoney(Number(walletTotals.plus || 0) + Number(walletTotals.extra || 0))}</p>
                    <p className="admin-card-note"><Gift size={13} /> {t("sections.users.p137")}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title">{t("sections.users.p138")}</p>
                    <p className="admin-card-value">{formatMoney(walletTotals.frozen)}</p>
                    <p className="admin-card-note"><WalletCards size={13} /> {t("sections.users.p139")}</p>
                </article>
            </section>

            <PageToolbar>
                <ToolbarInput
                    label={t("sections.users.p140")}
                    type="search"
                    placeholder={t("sections.users.p141")}
                    value={draftSearch}
                    onChange={(event) => setDraftSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") applySearch();
                    }}
                />
                <PageSizeSelect value={pageSize} options={PAGE_SIZE_OPTIONS} onChange={changePageSize} />
                <ToolbarButton onClick={applySearch}>{t("sections.users.p142")}</ToolbarButton>
                <ToolbarButton variant="secondary" onClick={clearSearch}>{t("sections.users.p83")}</ToolbarButton>
            </PageToolbar>

            {usersQuery.isError ? (
                <div className="admin-card admin-state" role="alert">
                    <strong>{t("sections.users.p143")}</strong>
                    <p>{mapUserApiError(usersQuery.error).message}</p>
                </div>
            ) : (
                <AdminTable columns={columns} rows={users} loading={usersQuery.isLoading} emptyText={t("sections.users.p144")} />
            )}

            {!usersQuery.isError && !usersQuery.isLoading && getCount(usersQuery.data) > 0 ? (
                <ServerPagination
                    count={getCount(usersQuery.data)}
                    page={page}
                    pageSize={pageSize}
                    previous={pageFromLink(usersQuery.data?.previous)}
                    next={pageFromLink(usersQuery.data?.next)}
                    pending={usersQuery.isFetching}
                    onPageChange={setPage}
                />
            ) : null}

            <UserCreateDialog
                open={createOpen}
                countryName={createCountryName}
                countryId={countryId}
                onCancel={() => setCreateOpen(false)}
                onCreated={(result) => {
                    const message = typeof result === "string" ? result : result?.message;
                    setCreatedMessage(typeof message === "string" ? message : t("sections.users.p145"));
                    queryClient.invalidateQueries({queryKey: ["admin-users", countryScope]});
                }}
            />
        </div>
    );
};
